import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { Building2, Phone, Mail, Globe, FileText } from 'lucide-react';
import { useCreateSupplier } from '../../hooks/useSuppliers';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const AddSupplierModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    website: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const createSupplier = useCreateSupplier();
  const { t } = useLanguage();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.name && formData.name.length > 191) {
      newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
    }

    if (formData.description && formData.description.length > 191) {
      newErrors.description = t(KEYS.COMMON_ERROR_MAX_CHARS);
    }

    if (formData.phone && formData.phone.length > 191) {
      newErrors.phone = t(KEYS.COMMON_ERROR_MAX_CHARS);
    }

    if (formData.email) {
      if (formData.email.length > 191) {
        newErrors.email = t(KEYS.COMMON_ERROR_MAX_CHARS);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t(KEYS.COMMON_ERROR_INVALID_EMAIL);
      }
    }

    if (formData.website) {
      if (formData.website.length > 191) {
        newErrors.website = t(KEYS.COMMON_ERROR_MAX_CHARS);
      } else {
        try {
          const url = new URL(formData.website);
          if (!url.protocol.startsWith('http')) {
            newErrors.website = t(KEYS.COMMON_ERROR_INVALID_URL);
          }
        } catch {
          newErrors.website = t(KEYS.COMMON_ERROR_INVALID_URL);
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createSupplier.mutateAsync(formData);
      
      setFormData({
        name: '',
        description: '',
        phone: '',
        email: '',
        website: '',
      });
      
      onClose();
    } catch (error) {
      if (error.code === 'VALIDATION_ERROR' && error.errors) {
        setErrors(normalizeFieldErrors(error.errors));
      } else {
        setSubmitError(error.message || t(KEYS.SUPPLIER_ADD_ERROR));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        description: '',
        phone: '',
        email: '',
        website: '',
      });
      setErrors({});
      setSubmitError('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t(KEYS.SUPPLIER_ADD_TITLE)}
      subtitle={t(KEYS.SUPPLIER_ADD_SUBTITLE)}
      size="medium"
      footer={
        <>
          <Button
            variant="outline"
            size="medium"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t(KEYS.COMMON_CANCEL)}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t(KEYS.SUPPLIER_ADD_SUBMITTING) : t(KEYS.SUPPLIER_ADD_SUBMIT)}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {submitError && (
          <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm">
            {submitError}
          </div>
        )}

        <div className="space-y-1">
          <label htmlFor="name" className="label-base flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`input-base ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter supplier name"
            maxLength={191}
          />
          {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
        </div>

        <div className="space-y-1">
          <label htmlFor="description" className="label-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className={`input-base resize-y ${errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter supplier description"
            rows={3}
            maxLength={191}
          />
          {errors.description && (
            <span className="text-xs text-red-500 mt-1">{errors.description}</span>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="phone" className="label-base flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`input-base ${errors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="+1 (555) 123-4567"
            maxLength={191}
          />
          {errors.phone && (
            <span className="text-xs text-red-500 mt-1">{errors.phone}</span>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="email" className="label-base flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`input-base ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="supplier@example.com"
            maxLength={191}
          />
          {errors.email && (
            <span className="text-xs text-red-500 mt-1">{errors.email}</span>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="website" className="label-base flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Website
          </label>
          <input
            type="url"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            className={`input-base ${errors.website ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="https://www.example.com"
            maxLength={191}
          />
          {errors.website && (
            <span className="text-xs text-red-500 mt-1">{errors.website}</span>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddSupplierModal;
